// ============================================================================
// AuthContext — single source of truth for the logged-in user's token + role.
// ============================================================================
// Replaces the prior prop-drilled `adminToken` string in MainApp.tsx.
//
// Storage: localStorage (Round 4 change). Previously sessionStorage, which
// is per-tab — so clicking "Public site" or "Room calendar" in a new tab
// would log the user out of the new tab. localStorage shares across every
// tab of the same origin, so admin auth survives target="_blank" links.
// Trade-off: tokens persist until explicit logout or browser data clear.
// For an internal UIF admin tool on trusted machines, this is the right
// trade-off — convenience > tab-close auto-logout.
//
// Usage:
//   <AuthProvider>           ← wrap once at the top of main.tsx
//     <App />
//   </AuthProvider>
//
//   In any component:
//     const { token, user, login, logout } = useAuth();
//     if (user?.role === 'uif') { ... }
//
// Backward compat: on first mount we read in this order:
//   1. localStorage[uif_auth]   ← new home
//   2. sessionStorage[uif_auth] ← legacy tab-scoped store (migrated up)
//   3. sessionStorage[uif_admin_token] ← pre-Phase-2 legacy
// First hit wins; lower hits are migrated up and their old keys cleared.
// ============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import type { AuthUser } from './api';

interface AuthState {
  token: string | null;
  user: AuthUser | null;
}

interface AuthContextValue extends AuthState {
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const STORAGE_KEY = 'uif_auth';
const LEGACY_TOKEN_KEY = 'uif_admin_token';

function readStored(): AuthState {
  if (typeof window === 'undefined') return { token: null, user: null };

  // 1. localStorage — the new home (Round 4). Shared across tabs.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.token && parsed?.user?.role) {
        return parsed as AuthState;
      }
    }
  } catch {
    /* fall through */
  }

  // 2. sessionStorage — Phase 2 location. Migrate up to localStorage so the
  //    current signed-in tab survives the storage change, then clear the
  //    sessionStorage copy so we don't keep two copies in sync.
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.token && parsed?.user?.role) {
        try { localStorage.setItem(STORAGE_KEY, raw); } catch { /* ignore */ }
        sessionStorage.removeItem(STORAGE_KEY);
        return parsed as AuthState;
      }
    }
  } catch {
    /* fall through */
  }

  // 3. Legacy migration: pre-Phase-2 stored just a token, no user/role info.
  //    Assume role='uif' (the only role that existed pre-Phase-2) and stub
  //    a minimal user so the new context is usable. Real user info will be
  //    populated on next login. Check both storages to maximize survival.
  const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY) || sessionStorage.getItem(LEGACY_TOKEN_KEY);
  if (legacyToken) {
    const migrated: AuthState = {
      token: legacyToken,
      user: {
        admin_user_id: 0,
        username: '',
        display_name: null,
        email: '',
        role: 'uif',
      },
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated)); } catch { /* ignore */ }
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    return migrated;
  }

  return { token: null, user: null };
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(readStored);

  const login = useCallback((token: string, user: AuthUser) => {
    const next: AuthState = { token, user };
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* localStorage might be disabled — state still works in memory */
    }
  }, []);

  const logout = useCallback(() => {
    setState({ token: null, user: null });
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      // Also wipe sessionStorage in case any legacy keys are still around.
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Cross-tab sync: if another tab logs out, this tab follows.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setState(readStored());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token: state.token,
      user: state.user,
      login,
      logout,
      isAuthenticated: !!state.token && !!state.user,
    }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}

// Convenience hooks
export function useRequireAuth(): AuthContextValue {
  const ctx = useAuth();
  if (!ctx.isAuthenticated) {
    throw new Error('Component requires authentication; render guard missing');
  }
  return ctx;
}
